import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, Monitor, CheckCircle } from 'lucide-react';

export default function InstallationGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            How to Install the PWA App
          </h1>
          <p className="text-lg text-muted-foreground">
            Follow these simple steps to install our Progressive Web App on your device
          </p>
        </div>

        {/* Benefits Section */}
        <Card className="mb-8 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Why Install?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <span className="text-3xl">⚡</span>
                <div>
                  <h4 className="font-semibold mb-1">Faster Access</h4>
                  <p className="text-sm text-muted-foreground">Launch directly from your home screen</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-3xl">📱</span>
                <div>
                  <h4 className="font-semibold mb-1">Native Experience</h4>
                  <p className="text-sm text-muted-foreground">Feels like a real app</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-3xl">🔔</span>
                <div>
                  <h4 className="font-semibold mb-1">Notifications</h4>
                  <p className="text-sm text-muted-foreground">Stay updated with alerts</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Installation Instructions */}
        <div className="space-y-6">
          {/* iOS Instructions */}
          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100">
              <CardTitle className="flex items-center gap-3">
                <span className="text-4xl">🍎</span>
                <div>
                  <div className="text-xl">iPhone & iPad (iOS)</div>
                  <div className="text-sm font-normal text-muted-foreground">Using Safari Browser</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Open Safari Browser</h4>
                    <p className="text-sm text-muted-foreground">Navigate to the PWA URL provided by your organization</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Tap the Share Button</h4>
                    <p className="text-sm text-muted-foreground">
                      Look for the <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 rounded">⬆️</span> icon at the bottom of the screen (or top-right on iPad)
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Select "Add to Home Screen"</h4>
                    <p className="text-sm text-muted-foreground">Scroll down in the menu and tap "Add to Home Screen"</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Tap "Add"</h4>
                    <p className="text-sm text-muted-foreground">Confirm the installation and the app icon will appear on your home screen</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Android Instructions */}
          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-green-50 to-green-100">
              <CardTitle className="flex items-center gap-3">
                <span className="text-4xl">🤖</span>
                <div>
                  <div className="text-xl">Android Devices</div>
                  <div className="text-sm font-normal text-muted-foreground">Using Chrome Browser</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Open Chrome Browser</h4>
                    <p className="text-sm text-muted-foreground">Navigate to the PWA URL provided by your organization</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Tap the Menu Button</h4>
                    <p className="text-sm text-muted-foreground">
                      Tap the three-dot menu <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded">⋮</span> in the top-right corner
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Select "Add to Home screen" or "Install app"</h4>
                    <p className="text-sm text-muted-foreground">The option may vary depending on your Android version</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Confirm Installation</h4>
                    <p className="text-sm text-muted-foreground">Tap "Add" or "Install" to complete the process</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Desktop Instructions */}
          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100">
              <CardTitle className="flex items-center gap-3">
                <Monitor className="w-10 h-10 text-purple-600" />
                <div>
                  <div className="text-xl">Desktop / Laptop</div>
                  <div className="text-sm font-normal text-muted-foreground">Chrome, Edge, or Brave Browser</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Open Your Browser</h4>
                    <p className="text-sm text-muted-foreground">Open Chrome, Edge, or Brave and navigate to the PWA URL</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Look for the Install Icon</h4>
                    <p className="text-sm text-muted-foreground">
                      You'll see an install icon (usually ⊕ or 🖥️) in the address bar on the right side
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Click "Install"</h4>
                    <p className="text-sm text-muted-foreground">Click the install icon and then click "Install" in the popup</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Launch the App</h4>
                    <p className="text-sm text-muted-foreground">The app will open in its own window and a shortcut will be created</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>❓ Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-1">Do I need to download from an app store?</h4>
              <p className="text-sm text-muted-foreground">No! PWAs install directly from your web browser. No app store needed.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Will it take up a lot of space?</h4>
              <p className="text-sm text-muted-foreground">PWAs are lightweight and take minimal storage compared to native apps.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Can I uninstall it later?</h4>
              <p className="text-sm text-muted-foreground">Yes! You can uninstall it just like any other app from your device settings.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Will I receive notifications?</h4>
              <p className="text-sm text-muted-foreground">Yes, once installed you can receive push notifications from the app (if you grant permission).</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Need help? Contact your organization administrator</p>
        </div>
      </div>
    </div>
  );
}
